import { useRef, useState } from "react";
import { formatNumber } from "@/app/(app)/_lib/format";

type Point = { x: number; y: number };

type Unit = { one: string; other: string };

type AreaChartProps = {
  data: number[];
  /** One label per data point. Length must match `data`. Used in the hover tooltip. */
  xLabels?: string[];
  /** Label shown at the left edge of the x-axis. */
  xStart: string;
  /** Label shown at the right edge of the x-axis. */
  xEnd: string;
  /** Singular / plural noun shown after the value in the hover tooltip. */
  unit?: Unit;
  /** Logical SVG height. Default 260. */
  height?: number;
};

/** Fixed-width gutter to the left of the SVG that holds y-tick labels. */
const Y_AXIS_WIDTH = 28;

export function AreaChart({ data, xLabels, xStart, xEnd, unit, height = 260 }: AreaChartProps) {
  const viewW = 600;
  const viewH = height;
  const padR = 4;
  const padT = 16;
  const padB = 32;
  const innerW = viewW - padR;
  const innerH = viewH - padT - padB;

  const max = Math.max(...data, 1);
  const niceMax = niceCeil(max);
  const stepX = data.length > 1 ? innerW / (data.length - 1) : 0;
  const points: Point[] = data.map((v, i) => ({
    x: i * stepX,
    y: padT + innerH - (v / niceMax) * innerH,
  }));

  const linePath = smoothPath(points);
  const baselineY = padT + innerH;
  const areaPath =
    points.length > 1
      ? `${linePath} L ${points[points.length - 1].x},${baselineY} L ${points[0].x},${baselineY} Z`
      : "";

  // niceCeil only ever returns 1, 2, 5, 10, 20, … For niceMax >= 5 the quarter
  // fractions round to distinct integer labels — the Figma-faithful uneven ticks
  // (e.g. 0·3·5·8·10), keep them. For a small max (1 or 2) the quarters collapse
  // to duplicate labels ("1,1,1,0,0" on real sparse data), so step by whole
  // integers instead. Found via live-data verification (a monitored set with max 1).
  const tickFractions =
    niceMax >= 5
      ? [0, 0.25, 0.5, 0.75, 1]
      : Array.from({ length: niceMax + 1 }, (_, i) => i / niceMax);
  const yTicks = tickFractions.map((t) => ({
    y: padT + innerH - t * innerH,
    label: formatNumber(Math.round(niceMax * t)),
  }));

  const svgRef = useRef<SVGSVGElement | null>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const lastIndexRef = useRef(0);

  function handleMove(event: React.PointerEvent<SVGSVGElement>) {
    const svg = svgRef.current;
    if (!svg || data.length === 0) return;
    if (data.length === 1) {
      setHoverIndex(0);
      return;
    }
    const rect = svg.getBoundingClientRect();
    const px = event.clientX - rect.left;
    const scaledX = (px / rect.width) * viewW;
    const clamped = Math.min(Math.max(scaledX, 0), innerW);
    const idx = Math.round((clamped / innerW) * (data.length - 1));
    setHoverIndex(idx);
  }

  function handleLeave() {
    setHoverIndex(null);
  }

  if (hoverIndex != null) lastIndexRef.current = hoverIndex;
  const displayIndex = hoverIndex ?? lastIndexRef.current;
  const displayPoint = points[displayIndex];
  const displayLabel = xLabels?.[displayIndex];
  const visible = hoverIndex != null && displayPoint != null;
  const pointXPct = displayPoint ? (displayPoint.x / viewW) * 100 : 0;
  const pointYPct = displayPoint ? (displayPoint.y / viewH) * 100 : 0;
  const tooltipLeftPct = clamp(pointXPct, 12, 88);
  const guideTopPx = padT;
  const guideHeightPx = innerH;
  const hoverTransition = "left 100ms ease-out, top 100ms ease-out, opacity 140ms ease-out";

  return (
    <div className="flex w-full" style={{ height: viewH }}>
      {/* Y-axis label column — fixed width so the chart's left edge aligns
          with the card content (heading/subheading) instead of being pushed
          inward by viewBox-relative padding. */}
      <div className="relative shrink-0" style={{ width: Y_AXIS_WIDTH }}>
        {yTicks.map((t, i) => (
          <span
            key={i}
            aria-hidden
            className="pointer-events-none absolute right-1 text-[11px] leading-none text-grey-500"
            style={{ top: t.y, transform: "translateY(-50%)" }}
          >
            {t.label}
          </span>
        ))}
      </div>

      {/* Chart area — SVG, x-axis labels, and hover overlays. */}
      <div className="relative flex-1" style={{ height: viewH }}>
        <svg
          ref={svgRef}
          role="img"
          aria-label="Trend over time"
          viewBox={`0 0 ${viewW} ${viewH}`}
          className="absolute inset-0 h-full w-full"
          preserveAspectRatio="none"
          onPointerMove={handleMove}
          onPointerLeave={handleLeave}
        >
          <defs>
            <linearGradient id="dash-area-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--color-green-500)" stopOpacity="0.2" />
              <stop offset="100%" stopColor="var(--color-green-500)" stopOpacity="0" />
            </linearGradient>
          </defs>

          {yTicks.map((t, i) => (
            <line
              key={i}
              x1={0}
              x2={viewW - padR}
              y1={t.y}
              y2={t.y}
              stroke="var(--color-grey-200)"
              strokeDasharray="2 4"
              vectorEffect="non-scaling-stroke"
            />
          ))}

          {areaPath && <path d={areaPath} fill="url(#dash-area-fill)" />}
          <path
            d={linePath}
            fill="none"
            stroke="var(--color-green-700)"
            strokeWidth={1}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        <span
          aria-hidden
          className="pointer-events-none absolute text-[11px] leading-none text-grey-500"
          style={{ left: 0, bottom: 8 }}
        >
          {xStart}
        </span>
        <span
          aria-hidden
          className="pointer-events-none absolute text-[11px] leading-none text-grey-500"
          style={{ right: `${(padR / viewW) * 100}%`, bottom: 8 }}
        >
          {xEnd}
        </span>

        {/* Hover overlays — kept mounted so position transitions glide smoothly. */}
        <div
          aria-hidden
          className="pointer-events-none absolute border-l border-dashed border-grey-900-24"
          style={{
            left: `${pointXPct}%`,
            top: guideTopPx,
            height: guideHeightPx,
            opacity: visible ? 1 : 0,
            transition: hoverTransition,
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute size-2 rounded-full border-2 border-green-700 bg-white"
          style={{
            left: `${pointXPct}%`,
            top: `${pointYPct}%`,
            transform: "translate(-50%, -50%)",
            opacity: visible ? 1 : 0,
            transition: hoverTransition,
          }}
        />
        <div
          role="tooltip"
          aria-hidden={!visible}
          className="pointer-events-none absolute z-10 flex flex-col gap-0.5 whitespace-nowrap rounded-[4px] border border-grey-200 bg-white px-2 py-1.5 shadow-flat"
          style={{
            left: `${tooltipLeftPct}%`,
            top: `${pointYPct}%`,
            transform: "translate(-50%, calc(-100% - 12px))",
            opacity: visible ? 1 : 0,
            transition: hoverTransition,
          }}
        >
          {displayLabel && <span className="text-tiny text-grey-600">{displayLabel}</span>}
          <span className="text-small font-medium text-black">
            {data[displayIndex] != null ? formatNumber(data[displayIndex]) : ""}
            {unit && (
              <span className="ml-1 text-grey-600">
                {data[displayIndex] === 1 ? unit.one : unit.other}
              </span>
            )}
          </span>
        </div>
      </div>
    </div>
  );
}

export function Sparkline({ data }: { data: number[] }) {
  const w = 120;
  const h = 36;
  const max = Math.max(...data, 1);
  const stepX = data.length > 1 ? w / (data.length - 1) : 0;
  const points: Point[] = data.map((v, i) => ({
    x: i * stepX,
    y: h - (v / max) * (h - 4) - 2,
  }));
  const path = smoothPath(points);
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      className="shrink-0 text-green-700"
      aria-hidden
    >
      <path
        d={path}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function smoothPath(pts: Point[]): string {
  if (pts.length === 0) return "";
  if (pts.length === 1) return `M ${pts[0].x},${pts[0].y}`;
  let d = `M ${pts[0].x},${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
  }
  return d;
}

function niceCeil(n: number): number {
  if (n <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(n)));
  const norm = n / pow;
  let nice: number;
  if (norm <= 1) nice = 1;
  else if (norm <= 2) nice = 2;
  else if (norm <= 5) nice = 5;
  else nice = 10;
  return nice * pow;
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(Math.max(n, min), max);
}
